import {
	arrow,
	autoPlacement,
	autoUpdate,
	FloatingArrow,
	FloatingPortal,
	offset,
	OffsetOptions,
	Placement,
	Strategy,
	useFloating,
} from '@floating-ui/react';
import { mergeRefs } from '@react-aria/utils';
import { cloneElement, FC, JSX, MouseEvent, ReactElement, ReactNode, Ref, useId, useRef, useState } from 'react';

import { Text } from '../Typography/Text/Text';
import { StyledTooltip } from './Tooltip.styles';

export interface TooltipProps {
	/**
	 * Visually hidden text for screen readers
	 */
	accessibilityLabel?: string;
	/**
	 * Toggle whether the tooltip is visible
	 */
	active?: boolean;
	/**
	 * Toggle whether the tooltip is enabled
	 */
	enabled?: boolean;
	/**
	 * The react component to use as the
	 * trigger for the tooltip
	 */
	children: ReactElement;
	/**
	 * The content of the tooltip
	 */
	content: ReactNode;
	/**
	 * If `true`, the tooltip will show an arrow tip
	 */
	hasArrow?: boolean;
	/** Placement */
	placement?: Placement;
	/** Strategy */
	strategy?: Strategy;
	/**
	 * The offset of the tooltip
	 */
	offset?: OffsetOptions;
	/**
	 * Override the tooltip's stacking order. Defaults to the standard popover
	 */
	zIndex?: number;
}

export const Tooltip: FC<TooltipProps> = ({
	accessibilityLabel,
	active = false,
	enabled = true,
	children,
	content,
	hasArrow,
	placement = 'bottom',
	strategy = 'absolute',
	offset: offsetOptions = { mainAxis: 10, crossAxis: 0 },
	zIndex,
	...rest
}) => {
	const htmlId = useId();

	const arrowRef = useRef(null);

	const [isInTrigger, setInTrigger] = useState<boolean>(false);
	const [isInTooltip, setInTooltip] = useState<boolean>(false);
	const [isActive, setActive] = useState<boolean>(active);

	const enterTrigger = () => {
		setInTrigger(true);
		setActive(true);
	};

	const leaveTrigger = () => {
		setInTrigger(false);
		setActive(isInTooltip || active);
	};

	const enterTooltip = () => {
		setInTooltip(true);
		setActive(true);
	};

	const leaveTooltip = () => {
		setInTooltip(false);
		setActive(isInTrigger || active);
	};

	const { floatingStyles, refs, context } = useFloating({
		placement,
		strategy,
		middleware: [autoPlacement(), arrow({ element: arrowRef }), offset(offsetOptions)],
		whileElementsMounted: autoUpdate,
	});

	const childProps = children.props as {
		ref?: Ref<Element>;
		onMouseEnter?: (event: MouseEvent) => void;
		onMouseLeave?: (event: MouseEvent) => void;
	};

	const trigger: ReactElement = cloneElement(children, {
		ref: mergeRefs(refs.setReference, childProps.ref),
		onMouseEnter: (event: MouseEvent) => {
			childProps.onMouseEnter?.(event);
			enterTrigger();
		},
		onMouseLeave: (event: MouseEvent) => {
			childProps.onMouseLeave?.(event);
			leaveTrigger();
		},
		'aria-describedby': content && enabled ? htmlId : undefined,
	});

	if (!enabled) return trigger;

	return (
		<>
			{trigger}
			{content && (
				<FloatingPortal>
					<StyledTooltip
						ref={refs.setFloating}
						style={{
							...floatingStyles,
							visibility: isActive ? 'visible' : 'hidden',
							opacity: isActive ? 1 : 0,
							...(zIndex !== undefined ? { zIndex } : {}),
						}}
						role="tooltip"
						id={htmlId}
						aria-label={accessibilityLabel}
						{...(!isActive ? { 'aria-hidden': true } : {})}
						onMouseEnter={enterTooltip}
						onMouseLeave={leaveTooltip}
						{...rest}
					>
						<Text color="white" size="medium" weight="normal">
							{content}
						</Text>
						{hasArrow && <FloatingArrow ref={arrowRef} context={context} />}
					</StyledTooltip>
				</FloatingPortal>
			)}
		</>
	);
};

type WithTooltipProps = Omit<TooltipProps, 'content' | 'children'>;

export const withTooltip = (
	children: JSX.Element,
	content: string,
	enabled = true,
	tooltipProps?: WithTooltipProps
) => {
	const { hasArrow = true, placement = 'bottom', strategy = 'absolute', ...rest } = tooltipProps || {};
	return !enabled ? (
		children
	) : (
		<Tooltip content={content} hasArrow={hasArrow} strategy={strategy} placement={placement} {...rest}>
			{children}
		</Tooltip>
	);
};
